import { AlertTriangle, Loader2, Pencil, RefreshCw, X } from 'lucide-react';
import { useId, type SyntheticEvent } from 'react';
import { cn } from '@/lib/infra/utils';

export interface OrderActionButtonsProps {
  disabled?: boolean;
  canceling?: boolean;
  onEdit: () => void;
  onCancel: () => void | Promise<void>;
  className?: string;
  /** Hide edit when only cancellation is proven safe. */
  editAvailable?: boolean;
  /** #748 — terminal, unconfirmed and retryable. The control stays LIVE (that
   *  is the point: the trader gets it back), but must not look untouched, or
   *  "never clicked" and "asked, and we cannot confirm" are indistinguishable.
   *  Ignored while `canceling`, which is the stronger state. */
  unconfirmed?: boolean;
  /** #748 — overrides the cancel control's title/aria when the caller holds a
   *  more specific sentence than this component can derive (the Polymarket
   *  fence's timeout message). */
  cancelTitle?: string;
  /** #1226 — Cancel shown but not usable now (a Polymarket US repeat-cancel
   *  wait, a blocked cancel): the title says why. K/P rows never set it. */
  cancelDisabled?: boolean;
  /** #1226 — Edit shown greyed with `editTitle` as its reason (#1113). */
  editDisabled?: boolean;
  /** #1226 — replaces the edit control's title/aria ("Edit order", or why
   *  it can't be used now). Default: the K/P "Edit price". */
  editTitle?: string;
  /** #1226 — a third icon, Check status (a Polymarket US read of the order;
   *  never a cancel or resend). Absent on K/P rows. */
  onCheckStatus?: () => void;
  checkStatusTitle?: string;
  /** Accessible result of a failed status read; no extra visible layout. */
  checkStatusMessage?: string;
  /** A failed native read uses the same-sized warning glyph; it is not a cancel failure. */
  checkStatusFailed?: boolean;
  /** That read is in flight. */
  checkingStatus?: boolean;
}

export function OrderActionButtons({
  disabled = false,
  canceling = false,
  onEdit,
  onCancel,
  className,
  editAvailable = true,
  unconfirmed = false,
  cancelTitle,
  cancelDisabled = false,
  editDisabled = false,
  editTitle = 'Edit price',
  onCheckStatus,
  checkStatusTitle = 'Check status',
  checkStatusMessage,
  checkStatusFailed = false,
  checkingStatus = false,
}: OrderActionButtonsProps) {
  const statusMessageId = useId();
  const stop = (e: SyntheticEvent) => {
    e.stopPropagation();
  };

  // #241: `canceling` outlives the DELETE — it stays true until an authoritative
  // Open Orders snapshot drops the ID. Keeping the "Cancel order" name through
  // that window reads as actionable on a control that is deliberately inert.
  const cancelLabel =
    cancelTitle ??
    (canceling ? 'Cancel pending — waiting for Open Orders' : 'Cancel order');

  return (
    <div
      className={cn('inline-flex items-center gap-1', className)}
      onClick={stop}
      onPointerDown={stop}
    >
      {editAvailable && <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onEdit();
        }}
        disabled={disabled || canceling || editDisabled}
        title={editTitle}
        aria-label={editTitle}
        className="text-muted-foreground hover:bg-muted hover:text-foreground inline-flex h-6 w-6 items-center justify-center rounded transition-colors disabled:opacity-50"
      >
        <Pencil size={13} />
      </button>}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          void onCancel();
        }}
        disabled={disabled || canceling || cancelDisabled}
        title={cancelLabel}
        aria-label={cancelLabel}
        className={cn(
          'inline-flex h-6 w-6 items-center justify-center rounded transition-colors disabled:opacity-50',
          // Amber is the house could-not-confirm tone (the #241 timeout notices
          // use it); red stays the ordinary destructive-hover affordance.
          !canceling && unconfirmed
            ? 'text-amber-400 hover:bg-amber-500/10 hover:text-amber-300'
            : 'text-muted-foreground hover:bg-red-500/10 hover:text-red-400',
        )}
      >
        {canceling ? (
          <Loader2 size={13} className="animate-spin" />
        ) : unconfirmed ? (
          <AlertTriangle size={13} />
        ) : (
          <X size={14} />
        )}
      </button>
      {onCheckStatus && <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onCheckStatus();
        }}
        disabled={disabled || canceling || checkingStatus}
        title={checkStatusTitle}
        aria-label={checkStatusTitle}
        aria-busy={checkingStatus || undefined}
        aria-describedby={checkingStatus || checkStatusMessage ? statusMessageId : undefined}
        data-testid="order-check-status"
        className={cn('inline-flex h-6 w-6 items-center justify-center rounded transition-colors disabled:opacity-50',
          checkStatusFailed && !checkingStatus
            ? 'text-amber-400 light:text-amber-700 hover:bg-amber-500/10'
            : 'text-muted-foreground hover:bg-muted hover:text-foreground')}
      >
        {checkStatusFailed && !checkingStatus ? <AlertTriangle size={12} />
          : <RefreshCw size={12} className={checkingStatus ? 'animate-spin' : undefined} />}
      </button>}
      {onCheckStatus && <span id={statusMessageId} role={checkingStatus || checkStatusMessage ? 'status' : undefined}
        aria-live="polite" className="sr-only">
        {checkingStatus ? 'Checking status…' : checkStatusMessage}
      </span>}
    </div>
  );
}

export default OrderActionButtons;
